import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { GoogleMap, Polygon as GooglePolygon, Marker } from '@react-google-maps/api'
import * as turf from '@turf/turf'
import { kml } from '@tmcw/togeojson'
import { GridMalha, GridPolygon, MalhaGrid } from './components/gerarMalha'
import Configuracoes from './components/config'
import { calcularCentroTurf, criarPoligono, baixarGridKML, gerarArquivoShp } from './meta'
import { calculoAreaCoord } from '../../../services/Afins'
import { errorAlert, warningAlert } from '../../../utils/alert'
import { Render } from '../../../Components/CustomUi/render'
import Carregando from '../../../Components/CustomUi/loading'

const GridMap = () => {
  const mapRef = useRef<google.maps.Map | null>(null)
  const polygonRef = useRef<google.maps.Polygon | null>(null)

  const [loadingKmlAdjustment, setLoadingKmlAdjustment] = useState(false)

  const [kmlComCruzamento, setKmlComCruzamento] = useState(null)
  const [disable, setDisable] = useState(true)
  const [validoFile, setValidoFile] = useState(false)
  const [area, setArea] = useState([])
  const [editandoKml, setEditandoKml] = useState<boolean>(false)
  const [pontosKinks, setPontosKinks] = useState<{ lat: number; lng: number }[]>([])

  //GRID
  const [rotation, setRotation] = useState<number>(0)
  const [movendo, setMovendo] = useState<boolean>(false)
  const [areaHa, setAreaHa] = useState<number>(0)
  const [gridLevel, setGridLevel] = useState<string>('')
  const [position, setPosition] = useState<{ x: 0; y: 0 }>({ x: 0, y: 0 })
  const [grid, setGrid] = useState<GridMalha[]>([])
  const [gridOriginal, setGridOriginal] = useState<GridMalha[]>([])
  const [gridOriginalRotacao, setGridOriginalRotacao] = useState<GridMalha[]>([])
  const [malhaPolygon, setMalhaPolygon] = useState<GridPolygon[]>([])
  const [malhaPolygonOriginal, setMalhaPolygonOriginal] = useState<GridPolygon[]>([])
  const [tamanhoGridOriginal, setTamanhoGridOriginal] = useState<number>(0)
  const [abrirModal, setAbrirModal] = useState<boolean>(false)
  const [pontosSelecionados, setPontosSelecionados] = useState([])
  const [nomeTalhao, setNomeTalhao] = useState<string>('')
  const [clicou, setClicou] = useState<boolean>(false)
  const [mousePosition, setMousePosition] = useState<[number, number] | null>(null)
  const [mapClickEvent, setMapClickEvent] = useState<{ lat: number; lng: number; ts: number } | null>(null)

  //CONFIGURAÇÕES
  const [tab_container_atual, setTab_container_atual] = useState<string>('Mapa')
  const [novoPonto, setNovoPonto] = useState<boolean>(false)
  const [moverPonto, setMoverPonto] = useState<boolean>(false)
  const [removerPonto, setRemoverPonto] = useState<boolean>(false)
  const [selecionarPonto, setSelecionarPonto] = useState<boolean>(false)
  const [abaAtual, setAbaAtual] = useState<number>(0)
  const abasNome = ['Mapa', 'Pontos', 'Ordenacao']
  const [confirmado, setConfirmado] = useState<boolean>(false)
  const [arquivoSelecionado, setArquivoSelecionado] = useState<string>('')
  const [rotacaoInput, setRotacaoInput] = useState<string>('')
  const [reset, setReset] = useState(true)
  const [quadrante, setQuadrante] = useState<number>(0)
  const [kinks, setKinks] = useState<turf.FeatureCollection<turf.Point> | null>(null)

  const padrao = useMemo(() => [
    [-15.7801, -47.9292],
    [-15.7801, -47.9192],
    [-15.7701, -47.9192],
    [-15.7701, -47.9292],
    [-15.7801, -47.9292],
  ], [])

  const kmlCoordsLatLng = useMemo(() => {
    if (area.length <= 0) return padrao
    return area.map((item) => [item.lat, item.lng])
  }, [area, padrao])

  // ancora retorna [lng, lat] do turf — convertemos para {lat, lng}
  const ancoraRaw = useMemo(() => calcularCentroTurf(kmlCoordsLatLng), [kmlCoordsLatLng])
  const ancora = useMemo(() => {
    if (!ancoraRaw) return null
    return { lat: ancoraRaw[1], lng: ancoraRaw[0] }
  }, [ancoraRaw])

  const polygonKml = useMemo(() => criarPoligono(kmlCoordsLatLng), [kmlCoordsLatLng])

  const aoCarregarArquivo = useCallback((rawData: string, base64Data) => {
    extrairCoordenadas(rawData)
  }, [])

  const converterXmlDeString = async (xmlString: string): Promise<Document> => {
    const parser = new DOMParser()
    const xmlDoc = parser.parseFromString(xmlString, 'application/xml')
    return xmlDoc
  }

  const extrairCoordenadas = async (kmlString: string) => {
    try {
      const domKml = await converterXmlDeString(kmlString)

      const dataGeoJson = kml(domKml)
      setNomeTalhao(dataGeoJson?.features[0]?.properties?.name)
      const talhoes = []

      const dados = dataGeoJson.features
      const filtrado = dados.filter((data) => data.geometry.type === 'Polygon')

      if (filtrado.length > 0) {
        for (let i = 0; i < filtrado.length; i += 1) {
          const fragment = filtrado[i]
          const talhaoPoly = []

          const coordinates = fragment.geometry.coordinates[0]

          for (let ii = 0; ii < coordinates.length; ii += 1) {
            const coord = coordinates[ii]
            talhaoPoly.push({ lat: coord[1], lng: coord[0] })
          }

          talhoes.push(talhaoPoly)
        }

        const areaCoord = talhoes[0].map((item) => ({ lat: item.lat, lng: item.lng }))
        setArea(areaCoord)
        const calcArea = calculoAreaCoord([areaCoord])
        setAreaHa(calcArea)
        if (calcArea <= 0) {
          errorAlert('Arquivo Kml inválido!')
          setValidoFile(false)
          setTimeout(() => { setDisable(true) }, 1000)
        } else {
          setValidoFile(true)
          setTimeout(() => { setDisable(false) }, 1000)
        }
      } else {
        errorAlert('Arquivo kml inválido!')
        setValidoFile(false)
      }
    } catch (error) {
      console.error('Erro ao processar XML:', error)
    }
  }

  const verificarAvancar = useCallback(() => {
    const temValor = grid.some((item) => 'posicao' in item)
    const tamanhoGrid = grid.filter(({ center }) =>
      turf.booleanPointInPolygon(turf.point([center[1], center[0]]), polygonKml)
    )
    if (temValor && abaAtual === 2) {
      return setAbrirModal(true)
    }
    if (tamanhoGridOriginal !== tamanhoGrid.length && abaAtual === 1) {
      return setAbrirModal(true)
    }
    voltar()
  }, [grid, polygonKml, abaAtual, tamanhoGridOriginal])

  const avancar = useCallback(() => {
    if (abaAtual < abasNome.length - 1) {
      setAbaAtual((prev) => prev + 1)
    }
  }, [abaAtual, abasNome.length])

  const voltar = useCallback(() => {
    if (abaAtual > 0) {
      setAbaAtual((prev) => prev - 1)
    }
  }, [abaAtual])

  const resetarGrid = useCallback(() => {
    setReset(false)
    setGrid(gridOriginalRotacao)
    setPontosSelecionados([])
    setMousePosition(null)
  }, [gridOriginalRotacao])

  // Baixar arquivo (KML ou SHP) sem fechar/limpar
  const baixarMalha = useCallback((tipo: string) => {
    if (tipo === 'Kml') {
      baixarGridKML(grid, nomeTalhao)
    } else {
      gerarArquivoShp(grid, nomeTalhao)
    }
  }, [grid, nomeTalhao])

  // Salvar malha — no módulo, apenas reseta o estado (não há pai para notificar)
  const salvarMalha = useCallback(() => {
    // Baixa automaticamente o arquivo ao salvar
    if (arquivoSelecionado === 'Kml') {
      baixarGridKML(grid, nomeTalhao)
    } else if (arquivoSelecionado) {
      gerarArquivoShp(grid, nomeTalhao)
    }

    // Resetar estado
    setArquivoSelecionado('')
    setAbaAtual(0)
    setTab_container_atual('Mapa')
    setGrid([])
    setPontosSelecionados([])
  }, [grid, nomeTalhao, arquivoSelecionado])

  useEffect(() => {
    if (confirmado) {
      if (abaAtual === 2) {
        voltar()
        setAbrirModal(false)
        setSelecionarPonto(false)
        resetarGrid()
        setArquivoSelecionado('')
        setTimeout(() => { setConfirmado(false) }, 500)
      }
      if (abaAtual === 1) {
        voltar()
        setAbrirModal(false)
        setGrid(gridOriginal)
        setRotation(0)
        setRotacaoInput('0')
        setNovoPonto(false)
        setMoverPonto(false)
        setRemoverPonto(false)
        setTimeout(() => { setConfirmado(false) }, 500)
      }
    }
  }, [confirmado])

  useEffect(() => {
    if (area.length > 0) {
      const timer = setTimeout(() => {
        const kinks = turf.kinks(polygonKml)

        if (kinks.features.length > 0) {
          const pontos = kinks.features.map((f) => ({
            lat: f.geometry.coordinates[1],
            lng: f.geometry.coordinates[0],
          }))
          warningAlert(`O contorno possui ${pontos.length} vértice(s) sobreposto(s), habilite o editar contorno para ajustar`)
          setPontosKinks(pontos)
          setKmlComCruzamento(true)
          setLoadingKmlAdjustment(false)
          return
        }

        setPontosKinks([])
        setEditandoKml(false)
        setGrid([])
        setGridOriginal([])
        setGridOriginalRotacao([])
        setTamanhoGridOriginal(null)
        setClicou(false)
        setRotation(0)
        setKmlComCruzamento(false)
        setLoadingKmlAdjustment(false)
      }, 100)

      return () => clearTimeout(timer)
    }
  }, [area, polygonKml])

  // Salvar contorno editado manualmente
  const salvarContorno = useCallback(() => {
    if (!polygonRef.current) return
    setLoadingKmlAdjustment(true)
    requestAnimationFrame(() => {
      const path = polygonRef.current?.getPath()
      if (!path) return
      const novaArea: { lat: number; lng: number }[] = []
      for (let i = 0; i < path.getLength(); i++) {
        const pt = path.getAt(i)
        novaArea.push({ lat: pt.lat(), lng: pt.lng() })
      }
      if (novaArea.length > 0) {
        const first = novaArea[0]
        const last = novaArea[novaArea.length - 1]
        if (first.lat !== last.lat || first.lng !== last.lng) {
          novaArea.push({ ...first })
        }
      }
      setArea(novaArea)
    })
  }, [])

  // Verificar kinks diretamente a partir do path atual do polígono no mapa
  const verificarKinksDoPoligono = useCallback(() => {
    if (!polygonRef.current) return

    const path = polygonRef.current.getPath()
    const coords: [number, number][] = []
    for (let i = 0; i < path.getLength(); i++) {
      const pt = path.getAt(i)
      coords.push([pt.lng(), pt.lat()])
    }

    // Fechar o anel se necessário
    if (coords.length >= 3) {
      const first = coords[0]
      const last = coords[coords.length - 1]
      if (first[0] !== last[0] || first[1] !== last[1]) {
        coords.push([...first])
      }
    }

    if (coords.length < 4) return

    const poly = turf.polygon([coords])
    const kinks = turf.kinks(poly)

    if (kinks.features.length > 0) {
      // Ainda tem cruzamentos — atualizar apenas os markers
      const pontos = kinks.features.map((f) => ({
        lat: f.geometry.coordinates[1],
        lng: f.geometry.coordinates[0],
      }))
      setPontosKinks(pontos)
    } else {
      // Sem cruzamentos — salvar contorno automaticamente e sair do modo edição
      setPontosKinks([])
      setKmlComCruzamento(false)
      setEditandoKml(false)

      // Atualizar area com o path atual
      const novaArea: { lat: number; lng: number }[] = []
      for (let i = 0; i < path.getLength(); i++) {
        const pt = path.getAt(i)
        novaArea.push({ lat: pt.lat(), lng: pt.lng() })
      }
      if (novaArea.length > 0) {
        const first = novaArea[0]
        const last = novaArea[novaArea.length - 1]
        if (first.lat !== last.lat || first.lng !== last.lng) {
          novaArea.push({ ...first })
        }
      }
      setArea(novaArea)
    }
  }, [])

  // Remover vértice ao clicar com o botão direito
  const removerVertice = useCallback((e: google.maps.PolyMouseEvent) => {
    if (!editandoKml) return
    if (e.vertex == null) return
    if (!polygonRef.current) return

    const path = polygonRef.current.getPath()
    // Mínimo de 3 vértices para manter um polígono válido
    if (path.getLength() <= 3) {
      warningAlert('Não é possível remover: o polígono precisa ter no mínimo 3 vértices.')
      return
    }

    path.removeAt(e.vertex)
    verificarKinksDoPoligono()
  }, [editandoKml, verificarKinksDoPoligono])

  // Ao mover um vértice manualmente (arrastar), re-verificar kinks
  const aoEditarPoligono = useCallback(() => {
    if (!editandoKml) return
    verificarKinksDoPoligono()
  }, [editandoKml, verificarKinksDoPoligono])

  useEffect(() => {
    abasNome.forEach((item, index) => {
      if (index === abaAtual) {
        setTab_container_atual(item)
      }
    })
    if (abaAtual === 1) {
      setSelecionarPonto(false)
      setPontosSelecionados([])
    }
    if (abaAtual === 2) {
      setSelecionarPonto(true)
      setNovoPonto(false)
      setMoverPonto(false)
      setRemoverPonto(false)
    }
  }, [abaAtual])

  // Ajustar bounds do mapa quando as coordenadas mudam (exceto durante edição de contorno)
  useEffect(() => {
    if (!mapRef.current || area.length === 0) return
    if (editandoKml) return
    const bounds = new google.maps.LatLngBounds()
    area.forEach(({ lat, lng }) => { bounds.extend({ lat, lng }) })
    mapRef.current.fitBounds(bounds)
  }, [area, editandoKml])

  const center = useMemo(() => {
    if (area.length <= 0) return { lat: -14.235, lng: -51.925 }
    if (ancora) return ancora
    return { lat: kmlCoordsLatLng[0][0], lng: kmlCoordsLatLng[0][1] }
  }, [kmlCoordsLatLng, ancora, area])

  // Paths do polígono KML para o Google Maps
  const kmlPolygonPaths = useMemo(() => {
    return kmlCoordsLatLng.map(([lat, lng]) => ({ lat, lng }))
  }, [kmlCoordsLatLng])

  const aoClicarNoMapa = useCallback(
    (e: google.maps.MapMouseEvent) => {
      if (!e.latLng) return
      setMapClickEvent({ lat: e.latLng.lat(), lng: e.latLng.lng(), ts: Date.now() })
    },
    []
  )

  const aoMoverMouseNoMapa = useCallback(
    (e: google.maps.MapMouseEvent) => {
      if (!e.latLng) return
      if (pontosSelecionados.length === 1) {
        setMousePosition([e.latLng.lat(), e.latLng.lng()])
      }
    },
    [pontosSelecionados]
  )

  const aoCarregarMapa = useCallback((map: google.maps.Map) => {
    mapRef.current = map
    if (area.length > 0) {
      const bounds = new google.maps.LatLngBounds()
      area.forEach(({ lat, lng }) => { bounds.extend({ lat, lng }) })
      map.fitBounds(bounds)
    }
  }, [area])

  return (
    <div style={{ display: 'flex', position: 'relative', overflow: 'hidden', height: '100vh' }}>
      <Configuracoes
        disable={disable}
        setDisable={setDisable}
        aoCarregarArquivo={aoCarregarArquivo}
        validoFile={validoFile}
        setValidoFile={setValidoFile}
        rotation={rotation}
        setRotation={setRotation}
        rotacaoInput={rotacaoInput}
        setRotacaoInput={setRotacaoInput}
        movendo={movendo}
        setMovendo={setMovendo}
        novoPonto={novoPonto}
        setNovoPonto={setNovoPonto}
        moverPonto={moverPonto}
        setMoverPonto={setMoverPonto}
        removerPonto={removerPonto}
        setRemoverPonto={setRemoverPonto}
        selecionarPonto={selecionarPonto}
        setSelecionarPonto={setSelecionarPonto}
        gridLevel={gridLevel}
        setGridLevel={setGridLevel}
        position={position}
        setPosition={setPosition}
        tab_container_atual={tab_container_atual}
        setTab_container_atual={setTab_container_atual}
        arquivoSelecionado={arquivoSelecionado}
        setArquivoSelecionado={setArquivoSelecionado}
        setGrid={setGrid}
        setGridOriginal={setGridOriginal}
        setGridOriginalRotacao={setGridOriginalRotacao}
        setMalhaPolygon={setMalhaPolygon}
        setMalhaPolygonOriginal={setMalhaPolygonOriginal}
        setTamanhoGridOriginal={setTamanhoGridOriginal}
        setQuadrante={setQuadrante}
        setClicou={setClicou}
        areaHectares={areaHa}
        polygon={polygonKml}
        kmlComCruzamento={kmlComCruzamento}
        editandoKml={editandoKml}
        setEditandoKml={setEditandoKml}
        grid={grid}
        abaAtual={abaAtual}
        abasNome={abasNome}
        pontosSelecionados={pontosSelecionados}
        area={area}
        areaTamanho={areaHa}
        quadrante={quadrante}
        avancar={avancar}
        voltar={verificarAvancar}
        resetarGrid={resetarGrid}
        nomeTalhao={nomeTalhao}
        salvarMalha={salvarMalha}
        baixarMalha={baixarMalha}
        salvarContorno={salvarContorno}
      />

      {/* @ts-ignore */}
      <GoogleMap
        onLoad={aoCarregarMapa}
        onClick={aoClicarNoMapa}
        onMouseMove={aoMoverMouseNoMapa}
        mapContainerStyle={{ height: '100%', width: '100%', position: 'relative', overflow: 'hidden' }}
        center={center}
        zoom={area.length > 0 ? 15 : 4}
        options={{
          mapTypeId: 'satellite',
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: false,
          disableDefaultUI: false,
        }}
        onRightClick={() => { }}
      >
        <MalhaGrid
          polygon={polygonKml}
          novoPonto={novoPonto}
          moverPonto={moverPonto}
          removerPonto={removerPonto}
          selecionarPonto={selecionarPonto}
          rotation={rotation}
          isSliding={movendo}
          gridLevel={Number(gridLevel.replace(',', '.'))}
          moverHorizontal={position?.x}
          moverVertical={position?.y}
          tab_selecionado={tab_container_atual}
          abaAtual={abaAtual}
          grid={grid}
          setGrid={setGrid}
          gridOriginal={gridOriginal}
          setGridOriginal={setGridOriginal}
          gridOriginalRotacao={gridOriginalRotacao}
          setGridOriginalRotacao={setGridOriginalRotacao}
          setTamanhoGridOriginal={setTamanhoGridOriginal}
          abrirModal={abrirModal}
          setAbrirModal={setAbrirModal}
          confirmado={confirmado}
          setConfirmado={setConfirmado}
          pontosSelecionados={pontosSelecionados}
          setPontosSelecionados={setPontosSelecionados}
          mousePosition={mousePosition}
          setMousePosition={setMousePosition}
          ancora={ancora}
          mapClickEvent={mapClickEvent}
        />

        {/* Polígono do KML — editável quando em modo edição */}
        <GooglePolygon
          paths={kmlPolygonPaths}
          editable={editandoKml}
          draggable={false}
          onLoad={(polygon) => { polygonRef.current = polygon }}
          onUnmount={() => { polygonRef.current = null }}
          onRightClick={removerVertice}
          onMouseUp={aoEditarPoligono}
          options={{
            fillOpacity: editandoKml ? 0.05 : 0,
            fillColor: editandoKml ? '#ff4444' : undefined,
            strokeColor: editandoKml ? '#ff4444' : kmlComCruzamento ? '#ff4444' : 'cyan',
            strokeWeight: editandoKml ? 3 : 2,
            clickable: editandoKml,
          }}
        />

        {/* Marcadores nos pontos de interseção */}
        {pontosKinks.map((pt, i) => (
          <Marker
            key={`kink_${i}`}
            position={pt}
            icon={{
              path: 'M 0,0 C -2,-20 -10,-22 -10,-30 A 10,10 0 1,1 10,-30 C 10,-22 2,-20 0,0 z',
              fillColor: '#ff0000',
              fillOpacity: 1,
              strokeColor: '#000',
              strokeWeight: 1,
              scale: 1,
              labelOrigin: new window.google.maps.Point(0, -28)
            }}
            label={{ text: '!', color: 'white', fontWeight: 'semi-bold' }}
          />
        ))}
      </GoogleMap>

      {/* Overlay de loading ao ajustar contorno */}
      <Render condition={loadingKmlAdjustment}>
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: 'rgba(255,255,255,0.7)',
          zIndex: 1000,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          flexDirection: 'column'
        }}>
          <Carregando animation legenda='Ajustando contorno...' justifyContent='center' height={100} />
        </div>
      </Render>
    </div>
  )
}

export default GridMap
